/**
 * Global Azure vs China Azure difference documentation
 * https://learn.microsoft.com/en-us/azure/china/resources-developer-guide
 */

const KENTIK_AZURE_AUTH_URL = 'https://check-azure.kntk.cloud/';
const KENTIK_CHINA_AZURE_AUTH_URL = 'https://check-azure-cn.kntk.cloud/';

const AZURE_MANAGEMENT_URL = 'https://management.azure.com/';
const AZURE_CHINA_MANAGEMENT_URL = 'https://management.chinacloudapi.cn/';

const AZURE_LOGIN_URL = 'https://login.microsoftonline.com/';
const AZURE_CHINA_LOGIN_URL = 'https://login.partner.microsoftonline.cn/';

const AZURE_VNET_FLOW_LOGS_CONTAINER = 'insights-logs-flowlogflowevent';
const AZURE_NSG_FLOW_LOGS_CONTAINER = 'insights-logs-networksecuritygroupflowevent';
/**
 * @param [location] {string}
 * @returns {boolean}
 */
function isChinaRegion(location) {
  return location?.includes('china');
}

function getKentikAzureAuthUrlByLocation(location) {
  return isChinaRegion(location) ? KENTIK_CHINA_AZURE_AUTH_URL : KENTIK_AZURE_AUTH_URL;
}

function getAzureManagementUrlByLocation(location) {
  return isChinaRegion(location) ? AZURE_CHINA_MANAGEMENT_URL : AZURE_MANAGEMENT_URL;
}

function getAzureLoginUrlByLocation(location) {
  return isChinaRegion(location) ? AZURE_CHINA_LOGIN_URL : AZURE_LOGIN_URL;
}

// -----------------------------------------------------------------------------
// Azure Subscription ID helpers
// -----------------------------------------------------------------------------
/**
 * Canonical Azure subscription ID regex (UUID v4 format-agnostic, just UUID shape)
 * @type {RegExp}
 */
const AZURE_SUBSCRIPTION_ID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Normalize, deduplicate and validate Azure subscription IDs.
 * Accepts only an array via subscription_ids.
 *
 * @param {Object} params
 * @param {string[]} [params.subscription_ids]
 * @param {number} [params.max=50] - Maximum allowed subscription IDs to guard against abuse.
 * @returns {string[]} normalized & validated subscription IDs
 */
function normalizeAndValidateAzureSubscriptionIds({ subscription_ids, max = 50 } = {}) {
  const logger = global.logFactory.getLogger('azure');

  if (!Array.isArray(subscription_ids)) {
    logger.warn('normalizeAndValidateAzureSubscriptionIds: subscription_ids must be an array');
    return [];
  }

  let subs = subscription_ids
    .filter((s) => typeof s === 'string')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => s !== 'placeholder' && s !== '*' && !s.includes('0000'));

  subs = [...new Set(subs)];

  if (subs.length === 0) {
    logger.warn('normalizeAndValidateAzureSubscriptionIds: no valid subscription IDs after normalization');
    return [];
  }

  if (subs.length > max) {
    logger.warn(
      `normalizeAndValidateAzureSubscriptionIds: too many subscription IDs provided (${subs.length}) > max (${max})`
    );
    return [];
  }

  const invalid = subs.filter((id) => !AZURE_SUBSCRIPTION_ID_REGEX.test(id));
  if (invalid.length) {
    logger.warn('normalizeAndValidateAzureSubscriptionIds: invalid subscription ID(s) encountered', { invalid });
    return [];
  }

  return subs;
}

module.exports = {
  isChinaRegion,
  AZURE_MANAGEMENT_URL,
  AZURE_CHINA_MANAGEMENT_URL,
  getAzureLoginUrlByLocation,
  AZURE_NSG_FLOW_LOGS_CONTAINER,
  AZURE_VNET_FLOW_LOGS_CONTAINER,
  getKentikAzureAuthUrlByLocation,
  getAzureManagementUrlByLocation,
  // subscription ID helpers
  normalizeAndValidateAzureSubscriptionIds,
  AZURE_SUBSCRIPTION_ID_REGEX
};
